import React, { useState } from "react";
import { useNotify, useRefresh, Button as RaButton } from "react-admin";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  LinearProgress,
  List as MuiList,
  ListItem,
  ListItemText,
  Alert,
  Chip,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";

const getToken = () => JSON.parse(localStorage.getItem("token"));

const uploadMultiple = async (files) => {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));

  const res = await fetch(
    `${process.env.REACT_APP_URL}/admin/knowledgemanager/upload-multiple`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${getToken()}` },
      body: formData,
    }
  );

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.message || "Failed to upload files");
  }
  return data.files || [];
};

const embedBulk = async (fileUrls) => {
  const res = await fetch(
    `${process.env.REACT_APP_URL}/admin/knowledgemanager/bulk`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ fileUrls }),
    }
  );

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.message || "Failed to embed files");
  }
  return data;
};

const BulkUploadKnowledgeManager = () => {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [stage, setStage] = useState("select"); // select | uploading | embedding | done
  const [result, setResult] = useState(null); // { succeeded: [], failed: [] }
  const [error, setError] = useState(null);
  const notify = useNotify();
  const refresh = useRefresh();

  const handleClose = () => {
    if (stage === "uploading" || stage === "embedding") return;
    setOpen(false);
    setFiles([]);
    setStage("select");
    setResult(null);
    setError(null);
  };

  const handleFilesSelected = (e) => {
    setFiles(Array.from(e.target.files || []));
    setResult(null);
    setError(null);
  };

  const handleSubmit = async () => {
    if (files.length === 0) return;
    setError(null);
    try {
      setStage("uploading");
      const uploaded = await uploadMultiple(files);

      setStage("embedding");
      const fileUrls = uploaded.map((f) => ({ fileUrl: f.url }));
      const bulkResult = await embedBulk(fileUrls);

      const filenameByUrl = uploaded.reduce((acc, f) => {
        acc[f.url] = f.filename;
        return acc;
      }, {});

      setResult({
        succeeded: bulkResult.succeeded || [],
        failed: (bulkResult.failed || []).map((f) => ({
          ...f,
          filename: filenameByUrl[f.fileUrl] || f.fileUrl,
        })),
      });
      setStage("done");
      refresh();
    } catch (err) {
      setError(err.message || "Something went wrong");
      setStage("select");
    }
  };

  const busy = stage === "uploading" || stage === "embedding";

  return (
    <>
      <RaButton
        label="Bulk Upload"
        onClick={() => setOpen(true)}
        startIcon={<UploadFileIcon />}
      />
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Bulk Upload Documents</DialogTitle>
        <DialogContent dividers>
          {stage === "select" && (
            <>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Select multiple .txt files to upload and embed at once.
              </Typography>
              <input
                type="file"
                multiple
                accept=".txt"
                onChange={handleFilesSelected}
              />
              {files.length > 0 && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  {files.length} file(s) selected
                </Typography>
              )}
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </>
          )}

          {busy && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {stage === "uploading"
                  ? `Uploading ${files.length} file(s)...`
                  : "Embedding files into the vector store..."}
              </Typography>
              <LinearProgress />
            </>
          )}

          {stage === "done" && result && (
            <>
              <Alert severity={result.failed.length ? "warning" : "success"} sx={{ mb: 2 }}>
                {result.succeeded.length} succeeded, {result.failed.length} failed
              </Alert>
              {result.failed.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Failed files
                  </Typography>
                  <MuiList dense>
                    {result.failed.map((f, i) => (
                      <ListItem key={i} divider>
                        <ListItemText
                          primary={f.filename}
                          secondary={f.error}
                        />
                        <Chip label="Failed" color="error" size="small" />
                      </ListItem>
                    ))}
                  </MuiList>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {stage === "done" ? (
            <Button onClick={handleClose} variant="contained">
              Close
            </Button>
          ) : (
            <>
              <Button onClick={handleClose} disabled={busy}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={busy || files.length === 0}
              >
                Upload & Embed
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkUploadKnowledgeManager;
